import { Component, computed, effect, inject, signal } from "@angular/core"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { ToastService } from "src/app/services"
import { Column, PagedNews, TableLazyLoadEvent } from "../../models"
import { NewsService } from "../../services"
import { toPagedParams } from "../../helpers/TablePaging"
import { TableComponent } from "../../components/table/table.component"

@Component({
    selector: "valben-news-table",
    templateUrl: "./news-table.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [TableComponent],
})
export class NewsTablePage {
    private _newsService = inject(NewsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    cols: Column[] = [
        { field: "names", subField: "hr", header: "Naslov", bodyType: "langs", filterType: "text", filterMode: "contains", pairWith: "descriptions" },
        { field: "descriptions", subField: "hr", header: "Opis", bodyType: "langs", filterType: "text", filterMode: "contains" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "isActive", header: "Aktivan", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Aktivna", falseText: "Neaktivna", pairWith: "isDraft" },
        { field: "isDraft", header: "Skica", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Skica", falseText: "Bez skice" },
        { field: "link", header: "Link", bodyType: "link", filterType: "text", filterMode: "contains" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "imageUrl", header: "Slika", bodyType: "file", icon: "image" },
    ]

    private _pageRequest = signal<TableLazyLoadEvent | undefined>(undefined)

    news = httpResource<PagedNews>(() => {
        const event = this._pageRequest()
        return event ? this._newsService.PagedRequest(toPagedParams(event, this.cols)) : undefined
    })

    tableValues = computed(() =>
        (this.news.value()?.items ?? []).map(news => ({
            ...news,
            createdDate: new Date(news.createdDate),
            modifiedDate: new Date(news.modifiedDate),
        }))
    )

    totalRecords = computed(() => this.news.value()?.totalCount ?? 0)
    loading = computed(() => this.news.isLoading())

    refresh = () => this.news.reload()

    constructor() {
        effect(() => {
            if (!this.news.error()) return
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Došlo je do pogreške prilikom dohvata novosti.",
                life: 10000,
            })
        })
    }

    loadPage(event: TableLazyLoadEvent): void {
        setTimeout(() => this._pageRequest.set(event))
    }

    add(): void {
        this._router.navigate(["/news/add"])
    }

    edit(masterId: string): void {
        this._router.navigate(["/news/edit", masterId])
    }
}
